import { useCallback, useEffect, useState } from 'react';
// import { getWhitelistedEmails } from 'Api/uploaderApiFunctions'; // old email allow-list gate
import { publicAxios, endpoints } from 'Api';

const emit = () => {
  try {
    window.dispatchEvent(new Event('newUIFlagChange'));
  } catch {
    /* noop */
  }
};

// const FLAG_KEY = 'newUIEnabled'; // old email allow-list gate
const THEME_KEY = 'newUITheme';
// const EMAIL_KEY = 'userEmail'; // old email allow-list gate
// Session-scoped marker for the onboarding lane in progress. sessionStorage,
// not localStorage: a lane belongs to the tab that started it and must not
// leak into the next visit on the same browser.
const LANE_KEY = 'newUILane';
// Old email allow-list gate:
// // Cached copy of the server-side allow-list.
// const ALLOWED_EMAILS_KEY = 'newUIAllowedEmails';
// Last successful getConfig response. Read at boot so the design is known
// before the network answers; kept as-is whenever a refresh fails.
const CONFIG_KEY = 'ui.config';
// The user's own pick: 'true' = classic, 'false' = new. Absent means the
// backend's default_ui decides. Cleared on logout (see clearUiChoice).
const CLASSIC_KEY = 'ui.classic';
// Used until the backend has told us its default_ui.
const NEW_UI_DEFAULT = true;
const CONFIG_TIMEOUT_MS = 5000;
// With a cached config already on hand, boot only waits this long for a fresh
// one before rendering from the cache.
const CACHED_BOOT_WAIT_MS = 1500;

// // Leftovers from the email allow-list gate, which getConfig replaced.
// try {
//   localStorage.removeItem('newUIAllowedEmails');
//   Object.keys(localStorage)
//     .filter((key) => key === 'newUIEnabled' || key.startsWith('newUIEnabled:'))
//     .forEach((key) => localStorage.removeItem(key));
// } catch {
//   /* noop */
// }

// ---------------------------------------------------------------------------
// Backend config (POST /getConfig)
//
//   available            'both' | 'new' | 'legacy' — designs this user may see
//   default_ui           'new' | 'legacy'          — used when there's no pick
//   option_to_switch_ui  boolean                   — show the switch toggle
// ---------------------------------------------------------------------------
const readConfig = () => {
  let raw = null;
  try {
    raw = JSON.parse(localStorage.getItem(CONFIG_KEY));
  } catch {
    /* unparseable cache — treat as absent */
  }
  const available = ['new', 'legacy'].includes(raw?.available) ? raw.available : 'both';
  const defaultUi = ['new', 'legacy'].includes(raw?.default_ui) ? raw.default_ui : null;
  return {
    available,
    defaultNew: defaultUi ? defaultUi === 'new' : NEW_UI_DEFAULT,
    optionToSwitch: raw?.option_to_switch_ui === true,
    // Backend kill-switch for the one-time "try the new UI" popup. Opt-in:
    // absent/false means the popup stays dark.
    showPopup: raw?.show_new_ui_popup === true,
  };
};

const hasCachedConfig = () => {
  try {
    return !!localStorage.getItem(CONFIG_KEY);
  } catch {
    return false;
  }
};

const saveConfig = (data) => {
  try {
    let previous = null;
    try {
      previous = JSON.parse(localStorage.getItem(CONFIG_KEY));
    } catch {
      /* ignore */
    }
    // An unrecognised default_ui must not wipe out the one we already had.
    const next = ['new', 'legacy'].includes(data?.default_ui)
      ? data
      : { ...data, default_ui: previous?.default_ui };
    localStorage.setItem(CONFIG_KEY, JSON.stringify(next));
  } catch {
    /* storage blocked — keep running on whatever we resolved */
  }
};

const readChoice = () => {
  try {
    const v = localStorage.getItem(CLASSIC_KEY);
    if (v === 'true') return false;
    if (v === 'false') return true;
  } catch {
    /* ignore */
  }
  return null;
};

// A forced design beats the user's pick; otherwise their pick beats the default.
const resolveNewUI = () => {
  const { available, defaultNew } = readConfig();
  if (available === 'new') return true;
  if (available === 'legacy') return false;
  const choice = readChoice();
  return choice !== null ? choice : defaultNew;
};

const readCanSwitch = () => {
  const { available, optionToSwitch } = readConfig();
  return optionToSwitch && available === 'both';
};

// Whether the backend wants the one-time new-UI invite popup shown at all.
const readShowPopup = () => readConfig().showPopup;

export const canSwitchUI = () => readCanSwitch();

// The design this page load runs on. Settled once at boot (bootUiConfig) and
// then frozen: a late getConfig answer, or a login mid-session, must never swap
// the whole app under the user. Only beginUiSwitch's reload changes it.
let appliedNewUI = resolveNewUI();
let designLocked = false;

const usableToken = (token) => (token && token !== 'undefined' && token !== 'null' ? token : null);
const storedToken = () => {
  try {
    return usableToken(localStorage.getItem('token'));
  } catch {
    return null;
  }
};

let lastConfigToken;

/**
 * Fetch getConfig for `token` (default: the stored one; signed out sends null)
 * and cache it. A failure leaves the cached config in effect. `applyDesign`
 * also re-resolves the design, but only until boot has finished.
 */
export const refreshConfig = async ({ token, applyDesign = false } = {}) => {
  const sent = token !== undefined ? usableToken(token) : storedToken();
  lastConfigToken = sent;
  try {
    const res = await publicAxios({
      url: endpoints.getConfig,
      data: { token: sent },
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      timeout: CONFIG_TIMEOUT_MS,
    });
    if (res?.status !== 200 || res?.data?.success !== true) return false;
    saveConfig(res.data);
    if (applyDesign && !designLocked) appliedNewUI = resolveNewUI();
    emit();
    return true;
  } catch {
    return false;
  }
};

/** Re-fetch only when the session token has changed since the last call. */
export const syncConfigForToken = () => {
  const current = storedToken();
  if (current === lastConfigToken) return;
  refreshConfig({ token: current });
};

/**
 * Called once before the first render. Waits for a fresh config (briefly when
 * a cached one exists), then locks the design for the rest of this page load.
 */
export const bootUiConfig = async () => {
  const wait = new Promise((resolve) => {
    setTimeout(resolve, hasCachedConfig() ? CACHED_BOOT_WAIT_MS : CONFIG_TIMEOUT_MS);
  });
  await Promise.race([refreshConfig({ applyDesign: true }), wait]);
  designLocked = true;
};

/** True when the design this page load runs on is no longer the one config + pick resolve to. */
export const designOutOfDate = () => resolveNewUI() !== appliedNewUI;

/** Logout: forget the pick so the next session falls back to default_ui. */
export const clearUiChoice = () => {
  try {
    localStorage.removeItem(CLASSIC_KEY);
  } catch {
    /* noop */
  }
};

// Old email allow-list gate — replaced by getConfig above. Kept for reference.
// // The allow-list now comes from the upload service (see refreshAllowedEmails
// // below) and is cached here. This seed is only a cold-start fallback: it is used
// // until the first successful fetch, so an allow-listed user isn't dropped to the
// // classic UI on a fresh browser or while the API is unreachable. Keep entries
// // lowercase.
// export const NEW_UI_ALLOWED_EMAILS = ['layne@projector.app', 'jia6@yopmail.com'];
//
// const readAllowedEmails = () => {
//   try {
//     const cached = JSON.parse(localStorage.getItem(ALLOWED_EMAILS_KEY));
//     // An empty array is a real answer ("nobody"), so only fall back to the seed
//     // when there is no usable cache at all.
//     if (Array.isArray(cached)) return cached;
//   } catch {
//     /* unparseable cache — fall through to the seed */
//   }
//   return NEW_UI_ALLOWED_EMAILS;
// };
//
// // Is a given email allowed to use the new UI?
// export const canUseNewUI = (email) => readAllowedEmails()
//   .includes((email || '').trim().toLowerCase());
//
// /**
//  * Pull the allow-list and cache it. Safe to call on every app start: a failure
//  * leaves the previous cache in place rather than locking anyone out, and the
//  * gate stays synchronous because everything reads the cache, never the network.
//  */
// export const refreshAllowedEmails = async () => {
//   try {
//     const emails = await getWhitelistedEmails();
//     localStorage.setItem(ALLOWED_EMAILS_KEY, JSON.stringify(emails));
//     emit();
//   } catch {
//     /* keep whatever is cached */
//   }
// };
//
// // Reads the email persisted by getMyProfile (app.jsx) so the gate works
// // synchronously in the non-reactive isNewUIEnabled() call.
// const emailAllowed = () => {
//   try {
//     return canUseNewUI(localStorage.getItem(EMAIL_KEY));
//   } catch {
//     return false;
//   }
// };
//
// const isSignedIn = () => {
//   try {
//     return !!localStorage.getItem('token');
//   } catch {
//     return false;
//   }
// };
//
// // The preference is stored per account. Two people share a browser far more
// // often than they share an opinion about the layout, and an allow-listed user
// // signing out must not leave the new UI switched on for whoever signs in next.
// const prefKey = (email) => `${FLAG_KEY}:${(email || '').trim().toLowerCase()}`;
// const readStoredPref = () => {
//   try {
//     const perAccount = localStorage.getItem(prefKey(localStorage.getItem(EMAIL_KEY)));
//     // Fall back to the old single-key flag once, so an allow-listed user who
//     // already opted in does not get reset by this change.
//     return (perAccount !== null ? perAccount : localStorage.getItem(FLAG_KEY)) === 'true';
//   } catch {
//     return false;
//   }
// };

// ---------------------------------------------------------------------------
// Onboarding lanes
//
// Both lanes start on /setpassword and are told apart exactly the way the page
// itself does it (set-password/index.jsx): `gift=true` is the gift recipient,
// anything else is a quick-user link. They used to outrank the backend config;
// readFlag no longer checks them, so getConfig decides here too. The lane
// marker is still written by /setpassword but nothing reads it for the design.
// (Old: They outrank the allow-list in both directions, because the person
// following the link has no account yet — there is no email to check.)
// ---------------------------------------------------------------------------
const SET_PASSWORD_PATH = '/setpassword';
const GIFT_PATH_PREFIXES = ['/gift', '/receiving-gift'];

const readLane = () => {
  try {
    return sessionStorage.getItem(LANE_KEY) || '';
  } catch {
    return '';
  }
};

/** Gift recipients stay on the classic UI for the whole lane. */
const inGiftLane = () => {
  try {
    const { pathname, search } = window.location;
    if (GIFT_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return true;
    if (pathname.startsWith(SET_PASSWORD_PATH)) {
      return new URLSearchParams(search).get('gift') === 'true';
    }
    return readLane() === 'gift';
  } catch {
    return false;
  }
};

/**
 * Quick-user links get the new UI whoever follows them. Read from the URL
 * rather than the marker on the first page, so the very first paint is already
 * correct — the marker only has to carry the lane through the steps that
 * follow (avatar → interview → quick-user-payment), whose URLs say nothing
 * about where the user came from.
 */
const inQuickUserLane = () => {
  try {
    const { pathname, search } = window.location;
    if (pathname.startsWith(SET_PASSWORD_PATH)) {
      return new URLSearchParams(search).get('gift') !== 'true';
    }
    return readLane() === 'quick';
  } catch {
    return false;
  }
};

/** Remembers which lane this tab is in, for the steps after /setpassword. */
export const markUiLane = (lane) => {
  try {
    if (lane) sessionStorage.setItem(LANE_KEY, lane);
    else sessionStorage.removeItem(LANE_KEY);
    emit();
  } catch {
    /* noop */
  }
};

export const clearUiLane = () => markUiLane('');

// Old gate (email allow-list):
// // Classic (production) UI is the default, and signed-out visitors always get it
// // — the login page has to look the same for everyone, since we cannot know who
// // is about to sign in. Past that, an allow-listed user's own stored preference
// // decides. The two onboarding lanes are the exceptions, and are checked first.
// const readFlag = () => {
//   try {
//     if (inGiftLane()) return false;
//     if (inQuickUserLane()) return true;
//     if (!isSignedIn()) return false;
//     if (!emailAllowed()) return false;
//     return readStoredPref();
//   } catch {
//     return false;
//   }
// };

// The backend config (and, where it allows a choice, the user's pick) settled
// at boot decides for every page, the gift and quick-user flows included.
const readFlag = () => {
  try {
    // Old: the onboarding lanes used to force a design, outranking the config.
    // if (inGiftLane()) return false;
    // if (inQuickUserLane()) return true;
    return appliedNewUI;
  } catch {
    return false;
  }
};

const readTheme = () => {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

export const isNewUIEnabled = () => readFlag();

// Saves the user's pick. Refused (returns false) when the backend doesn't let
// this user switch — a backstop behind the hidden toggles.
export const setNewUIEnabled = (val) => {
  if (!readCanSwitch()) return false;
  try {
    localStorage.setItem(CLASSIC_KEY, val ? 'false' : 'true');
    emit();
    return true;
  } catch {
    return false;
  }
};

export const setNewUITheme = (mode) => {
  try {
    localStorage.setItem(THEME_KEY, mode === 'dark' ? 'dark' : 'light');
    emit();
  } catch {
    /* noop */
  }
};

export const useNewUI = () => {
  const [enabled, setEnabled] = useState(readFlag());
  const [themeMode, setThemeModeState] = useState(readTheme());
  const [canSwitch, setCanSwitch] = useState(readCanSwitch());
  const [showPopup, setShowPopupState] = useState(readShowPopup());

  useEffect(() => {
    const sync = () => {
      setEnabled(readFlag());
      setThemeModeState(readTheme());
      setCanSwitch(readCanSwitch());
      setShowPopupState(readShowPopup());
    };
    window.addEventListener('newUIFlagChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('newUIFlagChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const enable = useCallback(() => setNewUIEnabled(true), []);
  const disable = useCallback(() => setNewUIEnabled(false), []);
  const toggleTheme = useCallback(() => {
    setNewUITheme(readTheme() === 'dark' ? 'light' : 'dark');
  }, []);
  const setTheme = useCallback((mode) => setNewUITheme(mode), []);

  return {
    enabled,
    themeMode,
    canSwitch,
    showPopup,
    enable,
    disable,
    toggleTheme,
    setTheme,
  };
};
